import get from 'lodash/get';
import set from 'lodash/set';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateComponent } from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import {
  Data,
  TableColumn,
} from '~/app/components/fixed-first-column-table/types';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';

const getInitialState = (
  currentComponent: any,
  componentPath: string,
  field: string,
  initialValue: any
) => {
  return get(currentComponent, `props.${componentPath}.${field}`, initialValue);
};

type UseDynamicFixedFirstColumnTableProps = {
  fieldPath?: string;
};

export const useDynamicFixedFirstColumnTable = ({
  fieldPath = '',
}: UseDynamicFixedFirstColumnTableProps = {}) => {
  const dispatch = useDispatch();
  const { componentPath } = useDynamicGrid();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const currentComponent = flatOutline?.[selectedComponentId];

  const [tableSettings, setTableSettings] = useState({
    centeredInParent: false,
    hideHeader: false,
    striped: false,
    tableStyle: '',
    transposed: false,
  });

  const [headers, setHeaders] = useState<TableColumn[]>([]);

  const [data, setData] = useState<Data[]>([]);

  const handleUpdateComponent = useCallback(
    (field: string, value: string | boolean | any[]) => {
      const fieldPathValue = fieldPath
        ? `props.${componentPath}.${fieldPath}.${field}` // If fieldPath is provided, update the nested field
        : `props.${componentPath}.${field}`; // If fieldPath is not provided, update the top-level field
      set(currentComponent, fieldPathValue, value);
      dispatch(updateComponent(currentComponent));
    },
    [currentComponent, componentPath, fieldPath, dispatch]
  );

  const handleCenteredInParentChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('tableSettings.centeredInParent', e.target.checked);
    setTableSettings({ ...tableSettings, centeredInParent: e.target.checked });
  };

  const handleHideHeaderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('tableSettings.hideHeader', e.target.checked);
    setTableSettings({ ...tableSettings, hideHeader: e.target.checked });
  };

  const handleStripedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('tableSettings.striped', e.target.checked);
    setTableSettings({ ...tableSettings, striped: e.target.checked });
  };

  const handleTableStyleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('tableSettings.tableStyle', e.target.value);
    setTableSettings({ ...tableSettings, tableStyle: e.target.value });
  };

  const handleTransposedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('tableSettings.transposed', e.target.checked);
    setTableSettings({ ...tableSettings, transposed: e.target.checked });
  };

  const handleAddHeader = () => {
    const newHeader = {
      key: `${headers.length + 1}`,
      header: '',
    };
    const newHeaders = [...headers, newHeader];

    // Add the new header as an attribute to all existing tableData rows
    const updatedTableData = data.map((row) => ({
      ...row,
      attributes: [
        ...row.attributes,
        {
          header: newHeader.key,
          value: '',
        },
      ],
    }));

    setHeaders(newHeaders);
    setData(updatedTableData);
    handleUpdateComponent('headers', newHeaders);
    handleUpdateComponent('data', updatedTableData);
  };

  const handleDeleteHeader = (index: number) => {
    const headerKeyToDelete = headers[index].key;
    const updatedHeaders = [...headers];
    updatedHeaders.splice(index, 1);

    // Remove attributes from tableData that match the deleted header key
    const updatedTableData = data.map((row) => ({
      ...row,
      attributes: row.attributes.filter(
        (attr) => attr.header !== headerKeyToDelete
      ),
    }));

    setHeaders(updatedHeaders);
    setData(updatedTableData);
    handleUpdateComponent('headers', updatedHeaders);
    handleUpdateComponent('data', updatedTableData);
  };

  const handleHeaderChange = (index: number, value: string) => {
    const updatedHeaders = [...headers];
    updatedHeaders[index].header = value;
    setHeaders(updatedHeaders);
    handleUpdateComponent('headers', updatedHeaders);
  };

  const handleAddData = () => {
    const newDataAttributes = headers.map((header) => ({
      header: header.key,
      value: '',
    }));
    const newData = {
      id: `${data.length + 1}`,
      attributes: newDataAttributes,
      highlight: false,
    };
    const updatedTableData = [...data, newData];
    setData(updatedTableData);
    handleUpdateComponent('data', updatedTableData);
  };

  const handleDeleteData = (index: number) => {
    const updatedTableData = [...data];
    updatedTableData.splice(index, 1);
    setData(updatedTableData);
    handleUpdateComponent('data', updatedTableData);
  };

  const handleHighlightChange = (index: number) => {
    const updatedTableData = [...data];
    updatedTableData[index].highlight = !updatedTableData[index].highlight;
    setData(updatedTableData);
    handleUpdateComponent('data', updatedTableData);
  };

  useEffect(() => {
    setTableSettings({
      centeredInParent: getInitialState(
        currentComponent,
        componentPath,
        'centeredInParent',
        false
      ),
      hideHeader: getInitialState(
        currentComponent,
        componentPath,
        'hideHeader',
        false
      ),
      striped: getInitialState(
        currentComponent,
        componentPath,
        'striped',
        false
      ),
      tableStyle: getInitialState(
        currentComponent,
        componentPath,
        'tableStyle',
        ''
      ),
      transposed: getInitialState(
        currentComponent,
        componentPath,
        'transposed',
        false
      ),
    });
    setHeaders(getInitialState(currentComponent, componentPath, 'headers', []));
    setData(getInitialState(currentComponent, componentPath, 'data', []));
  }, [currentComponent, componentPath, setTableSettings, setHeaders, setData]);

  return {
    currentComponent,
    componentPath,
    headers,
    data,
    tableSettings,
    handleAddHeader,
    handleAddData,
    handleCenteredInParentChange,
    handleDeleteData,
    handleDeleteHeader,
    handleHighlightChange,
    handleHeaderChange,
    handleHideHeaderChange,
    handleStripedChange,
    handleTableStyleChange,
    handleTransposedChange,
  };
};
